import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import AdminLayout from '../components/AdminLayout';
import './AdminDashboard.css';
import './AdminNotifications.css';

// Super-admin view of the email queue: what was sent, what is waiting, and what failed (with the reason), plus a
// Retry action for failed jobs. Row-level security limits the table to super-admins.
const TEMPLATES = {
  application_received: 'Application received (to parent)',
  admin_new_application: 'New application (to staff)',
  admin_new_enquiry: 'New enquiry (to staff)',
  status_change: 'Status update (to parent)'
};
const STATUSES = ['failed', 'pending', 'sending', 'sent', 'cancelled'];
const PAGE_SIZE = 100;

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '');

const AdminNotifications = () => {
  const location = useLocation();
  // The dashboard links here with ?status=failed; only known values are accepted.
  const [filter, setFilter] = useState(() => {
    const status = new URLSearchParams(location.search).get('status');
    return STATUSES.includes(status) ? status : '';
  });
  const [rows, setRows] = useState([]);
  const [failedCount, setFailedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('email_notifications')
        .select('id, recipient_email, subject, template_type, status, attempts, next_attempt_at, sent_at, last_error, created_at')
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);
      if (filter) query = query.eq('status', filter);
      const [list, failed] = await Promise.all([
        query,
        supabase.from('email_notifications').select('id', { count: 'exact', head: true }).eq('status', 'failed')
      ]);
      if (list.error) throw list.error;
      setRows(list.data || []);
      setFailedCount(failed.error ? 0 : failed.count || 0);
    } catch (err) {
      console.error('Failed to load notifications', err);
      setError('Failed to load email notifications');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const retry = async (row) => {
    setBusyId(row.id);
    setMessage({ type: '', text: '' });
    const { error: rpcError } = await supabase.rpc('retry_email_job', { p_id: row.id });
    if (rpcError) {
      setMessage({
        type: 'error',
        text: rpcError.code === '42501' ? 'Only super-admins can retry notifications' : 'Could not retry this notification'
      });
    } else {
      setMessage({ type: 'success', text: `Queued again: ${row.subject}` });
      await load();
    }
    setBusyId(null);
  };

  return (
    <AdminLayout>
      <div className="admin-dashboard admin-notifications">
        <div className="dashboard-header">
          <div>
            <h1>Email Notifications</h1>
            <p>Emails sent by the system, newest first. Failed emails are retried automatically up to five times.</p>
          </div>
        </div>

        {failedCount > 0 && (
          <div className="error-banner" role="alert">
            {failedCount} email{failedCount === 1 ? '' : 's'} could not be delivered.{' '}
            {filter !== 'failed' && (
              <button type="button" className="link-button" onClick={() => setFilter('failed')}>Show failed</button>
            )}
          </div>
        )}
        {message.text && <div className={`alert alert-${message.type}`} role="status">{message.text}</div>}

        <div className="notification-filters">
          <label htmlFor="notification-status">Status</label>
          <select id="notification-status" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading notifications...</p>
          </div>
        )}
        {error && <div className="error-banner">{error}</div>}

        {!loading && !error && (rows.length === 0 ? (
          <p className="no-data">{filter ? `No ${filter} notifications.` : 'No notifications yet.'}</p>
        ) : (
          <div className="applications-table-container">
            <table className="applications-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Type</th>
                  <th>To</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Details</th>
                  <th><span className="visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{when(row.created_at)}</td>
                    <td>{TEMPLATES[row.template_type] || row.template_type}</td>
                    <td>{row.recipient_email}</td>
                    <td>{row.subject}</td>
                    <td><span className={`notification-status status-${row.status}`}>{row.status}</span></td>
                    <td>{row.attempts}</td>
                    <td className="notification-details">
                      {row.status === 'sent' && `Sent ${when(row.sent_at)}`}
                      {row.status === 'pending' && row.attempts > 0 && `Next try ${when(row.next_attempt_at)}`}
                      {row.last_error && <div className="notification-error">{row.last_error}</div>}
                    </td>
                    <td>
                      {(row.status === 'failed' || row.status === 'cancelled') && (
                        <button type="button" className="btn-view" onClick={() => retry(row)} disabled={busyId === row.id}>
                          {busyId === row.id ? 'Retrying...' : 'Retry'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === PAGE_SIZE && <p className="help-text">Showing the newest {PAGE_SIZE}. Use the status filter to narrow the list.</p>}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
};

export default AdminNotifications;
